import type { Editor } from 'grapesjs';
import type { EditorProps } from '@grapesjs/react';
import React, { useCallback, useEffect, useState } from 'react';
import { hideLoader } from '../slices/admin/loader';
import { useAppDispatch } from '../hooks';
import { editorConfig } from './config';
import { setupReactAdapter } from './baseReactAdapter';
import { registerReactComponents } from './reactСomponents';
import { initializeBlocks } from './config/blocks';
import { publishProject } from '../thunks/admin/publishProject';
import { uploadImage } from '../services/uploadImage';
import { useApplicationContext } from '../utils/applicationContext';
import { useSelector } from 'react-redux';
import { selectConfig } from '../selectors/admin/config';
import { selectProjectTitle } from '../selectors/admin/projectTitle';
import { selectProjectId } from '../selectors/admin/projectId';
import { updateProjectConfig } from '../thunks/admin/updateProjectConfig';
import { copyStylesAndScripts } from './utils/copyStylesAndScripts';
import { initializeExtraPanels } from './config/panels';
import { initializeCrossTabCopy } from './commands/crossTabCopy';
import { initializeCrossTabPaste } from './commands/crossTabPaste';
import './index.css';

export const GrapesJsEditorContainer: React.FC = () => {
  const dispatch = useAppDispatch();

  const context = useApplicationContext();

  const { config, id: configId } = useSelector(selectConfig);
  const title = useSelector(selectProjectTitle);
  const id = useSelector(selectProjectId);

  const [previewLink, setPreviewLink] = useState<string>('#');

  useEffect(() => {
    setPreviewLink(`${window.location.href}/preview`);
  }, [id]);

  const [GrapesJsEditor, setGrapesJsEditor] = useState<React.ComponentType<EditorProps> | null>(null);
  const [grapesJs, setGrapesJs] = useState<(typeof import('grapesjs'))['default'] | null>(null);

  const onPublishClick = useCallback(() => {
    dispatch(publishProject(id));
  }, [dispatch, id]);

  const onUploadImage = useCallback(
    (file: File) => {
      return uploadImage(context, { file, projectId: id, configId });
    },
    [context, id, configId],
  );

  const onSaveClick = useCallback(
    (config: object) => {
      dispatch(updateProjectConfig({ id, config }));
    },
    [id, dispatch],
  );

  const onEditor = useCallback(
    (editor: Editor) => {
      // todo: в доке grapes сказано, что такие вещи лучше добавлять в плагины

      // Настраиваем адаптер для React-компонентов
      setupReactAdapter(editor, { isEditor: true, context });

      // Регистрируем React-компоненты
      registerReactComponents(editor);

      // Инициализируем блоки
      initializeBlocks(editor);

      // Добавляем дополнительные панели
      initializeExtraPanels(editor, { title, previewLink, onPublishClick, onSaveClick });

      // Инициализируем команды для кросс-табового копирования
      initializeCrossTabCopy(editor);
      initializeCrossTabPaste(editor);

      editor.on('load', () => {
        const iframe = editor.Canvas.getFrameEl();
        copyStylesAndScripts(iframe);
      });
    },
    [context, onPublishClick, onSaveClick, previewLink, title],
  );

  useEffect(() => {
    Promise.all([import('@grapesjs/react'), import('grapesjs'), import('grapesjs/dist/css/grapes.min.css')])
      .then(([reactModule, grapesJsModule]) => {
        setGrapesJsEditor(() => reactModule.default);
        setGrapesJs(() => grapesJsModule.default);
      })
      .catch(error => context.logger.error('Unable to load GrapesJS editor', error))
      .finally(() => dispatch(hideLoader()));
  }, [context.logger, dispatch]);

  if (!GrapesJsEditor || !grapesJs) {
    return null;
  }

  return (
    <GrapesJsEditor
      grapesjs={grapesJs}
      options={{
        ...editorConfig,
        projectData: config,
        assetManager: {
          upload: 'blob',
          uploadName: 'files',
          // @ts-expect-error Фактически в промисе должен возвращаться asset картинки
          customFetch: async (_url, options) => {
            const files = options.body.getAll('files[]');
            const results = await Promise.all(files.map(onUploadImage));

            const images = results.filter(Boolean).map(src => ({
              src,
              name: src.split('/').pop(),
            }));

            return { data: images };
          },
        },
      }}
      onEditor={onEditor}
    />
  );
};
